import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { deletePackagingList } from "@/lib/api";

/* Director-only: delete a mistaken packing list after typing DELETE. Blocked if any coil was used. */
export const DeletePackingListDialog = ({ pkg, onClose }) => {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await deletePackagingList(pkg.id, text.trim());
      toast.success(`Deleted ${r.tag} · ${r.packaging_list_number} — ${r.coils_removed} coil(s) removed`);
      ["packaging-lists", "coils", "stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object") setBlocked(d);
      else toast.error(d || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="delete-pkg-dialog"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()} onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}>
      <div className="bg-white rounded-sm w-full max-w-md p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-red-700 font-bold">Delete packing list</p>
            <h3 className="text-lg font-bold text-slate-900">{pkg.rm_tag_creation_id} · <span className="font-mono-data">{pkg.packaging_list_number}</span></h3>
            <p className="text-sm text-slate-500">{pkg.supplier_name} · {pkg.coil_count} coil(s)</p>
          </div>
          <button onClick={onClose} data-testid="delete-pkg-close" className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        {blocked ? (
          <div className="rounded-sm border border-red-200 bg-red-50 p-3 space-y-2" data-testid="delete-pkg-blocked">
            <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {blocked.message}</p>
            <ul className="text-xs text-red-900 space-y-0.5 max-h-48 overflow-y-auto">
              {blocked.blocked.map((b) => <li key={`${b.coil_id}-${b.reason}`}><span className="font-mono-data font-semibold">{b.coil_id}</span> — {b.reason}</li>)}
            </ul>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              This removes the packing list and all its coils from RM / SFG Inward and Inventory. A record of it is kept for the Director. Type <b className="font-mono-data">DELETE</b> to confirm.
            </p>
            <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Type DELETE" data-testid="delete-pkg-input"
              className="w-full h-11 px-3 border-2 border-slate-300 focus:border-red-500 outline-none rounded-sm font-mono-data tracking-widest" />
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="delete-pkg-cancel">{blocked ? "Close" : "Cancel"}</Button>
          {!blocked && (
            <Button onClick={run} disabled={text.trim() !== "DELETE" || busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-pkg-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
